// Project structure rules (pure, no Excel, no React). Ported from the customer's design
// (docs/customer-spec/02_project_structure, design/Structure.dc.html) so behaviour matches it.
//
// Site:  { name, area, naming: { rule }, ents: [Entity] }
// Entity { id, kind: "common" | "bldg" | "shared", name, tpl: "building" | "site" | "parking",
//          zones, down, up, typ (source building id), link, serves: [id] }
// Node:  { id, lib, name, en, cat, sys, on, hidden, split, sheet, loc, no, pno, kids: [Node] }
//   sys = system (leaf, an Excel block); on = false: switched off (no number, no sheet);
//   hidden: kept with its works but left out of Excel; split = false: "one sheet".

export const CATEGORIES = {
  hard: "CAPEX · პირდაპირი დანახარჯები (Hard Cost)",
  soft: "CAPEX · არაპირდაპირი დანახარჯები (Soft Cost)",
  opex: "საოპერაციო დანახარჯები (OPEX)",
};

export const LOCATION_RULES = {
  none: "ობიექტი",
  vol: "ობიექტი › ზონა",
  floor: "ობიექტი › ზონა › სართული",
  ztype: "ობიექტი › ზონა › სართული › ბინის ტიპი",
  system: "ობიექტი › სისტემა",
};

export const DEFAULT_MARKUPS = [
  { k: "oh", name: "ზედნადები ხარჯი", pct: 10, on: true, at: "sheet" },
  { k: "pr", name: "გეგმიური დაგროვება", pct: 8, on: true, at: "sheet" },
  { k: "ct", name: "გაუთვალისწინებელი ხარჯი", pct: 5, on: false, at: "total" },
  { k: "vat", name: "დ.ღ.გ.", pct: 18, on: true, at: "total" },
];

const SHEET_NAME_MAX = 31;
const FORBIDDEN_SHEET_CHARS = /[[\]:*?/\\]/;
const FORBIDDEN_SHEET_CHARS_G = /[[\]:*?/\\]/g;

// ---------- building names ----------

const GEO_LETTERS = "აბგდევზთიკლმნოპჟრსტუფქღყშჩცძწჭხჯჰ";
const ROMAN = [["C", 100], ["XC", 90], ["L", 50], ["XL", 40], ["X", 10], ["IX", 9], ["V", 5], ["IV", 4], ["I", 1]];

const toRoman = (n) =>
  ROMAN.reduce((acc, [sym, val]) => {
    while (n >= val) {
      acc += sym;
      n -= val;
    }
    return acc;
  }, "");

const fromRoman = (text) => {
  let n = 0;
  let i = 0;
  ROMAN.forEach(([sym, val]) => {
    while (text.substr(i, sym.length) === sym) {
      n += val;
      i += sym.length;
    }
  });
  return i === text.length && n ? n : NaN;
};

const SERIES_KINDS = [
  { ok: (t) => /^\d+$/.test(t), val: Number, str: (n, t) => String(n).padStart(t.length, "0") },
  { ok: (t) => /^[IVXLC]+$/.test(t) && !Number.isNaN(fromRoman(t)), val: fromRoman, str: toRoman },
  { ok: (t) => /^[A-Z]$/.test(t), val: (t) => t.charCodeAt(0) - 64, str: (n) => (n >= 1 && n <= 26 ? String.fromCharCode(64 + n) : String(n)) },
  { ok: (t) => /^[a-z]$/.test(t), val: (t) => t.charCodeAt(0) - 96, str: (n) => (n >= 1 && n <= 26 ? String.fromCharCode(96 + n) : String(n)) },
  { ok: (t) => t.length === 1 && GEO_LETTERS.includes(t), val: (t) => GEO_LETTERS.indexOf(t) + 1, str: (n) => GEO_LETTERS[n - 1] || String(n) },
];
const NUMBER_KIND = SERIES_KINDS[0];
const LATIN_UPPER_KIND = SERIES_KINDS[2];
const TOKEN_CHAR = /[0-9A-Za-zა-ჰ]/;

/**
 * Building names from the estimator's rule (names separated by commas). What changes between the
 * first two names continues: "ბლოკი A, ბლოკი B" → C; "B-01, B-02" → B-03; "კორპუსი 2, კორპუსი 4" → 6.
 * A single name continues from its number; a bare word gets numbers; plain names are used as written.
 * Returns { names, ok } — ok = false when the series was not recognised.
 */
export function seqNames(rule, count) {
  let items = String(rule || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  let prefix = "";
  let suffix = "";
  let start = null;
  let step = 1;
  let kind = null;

  if (items.length >= 2) {
    const [a, b] = items;
    let i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    while (i > 0 && TOKEN_CHAR.test(a[i - 1])) i--;
    let j = 0;
    while (j < a.length - i && j < b.length - i && a[a.length - 1 - j] === b[b.length - 1 - j]) j++;
    while (j > 0 && TOKEN_CHAR.test(a[a.length - j])) j--;
    const t0 = a.slice(i, a.length - j);
    const t1 = b.slice(i, b.length - j);
    kind = SERIES_KINDS.find((k) => k.ok(t0) && k.ok(t1)) || null;
    if (kind) {
      prefix = a.slice(0, i);
      suffix = a.slice(a.length - j);
      start = t0;
      step = kind.val(t1) - kind.val(t0) || 1;
    }
  } else if (items.length === 1) {
    const tokens = [];
    const re = /(^|[^0-9A-Za-zა-ჰ])([0-9]+|[A-Za-z]+|[ა-ჰ])(?=$|[^0-9A-Za-zა-ჰ])/g;
    let m;
    while ((m = re.exec(items[0]))) tokens.push({ t: m[2], at: m.index + m[1].length });
    const pick =
      tokens.filter((x) => /^\d+$/.test(x.t)).pop() || tokens.filter((x) => x.t.length === 1 || /^[IVXLC]+$/.test(x.t)).pop();
    if (pick) {
      kind =
        pick.t.length === 1 && /[A-Z]/.test(pick.t) && pick.t !== "I"
          ? LATIN_UPPER_KIND
          : SERIES_KINDS.find((k) => k.ok(pick.t)) || null;
      if (kind) {
        prefix = items[0].slice(0, pick.at);
        suffix = items[0].slice(pick.at + pick.t.length);
        start = pick.t;
      }
    } else {
      prefix = `${items[0]} `;
      start = "1";
      kind = NUMBER_KIND;
      items = [];
    }
  } else {
    prefix = "შენობა ";
    start = "1";
    kind = NUMBER_KIND;
  }

  const names = [];
  for (let k = 0; k < count; k++) {
    if (k < items.length) names.push(items[k]);
    else if (kind) names.push(prefix + kind.str(kind.val(start) + k * step, start) + suffix);
    else names.push(`შენობა ${k + 1}`);
  }
  return { names, ok: !!kind };
}

// ---------- site ----------

/** Buildings only; one building means no common object, no parking and no object prefixes. */
export function isSingleBuilding(site) {
  return (site?.ents || []).filter((e) => e.kind === "bldg").length === 1;
}

/** Objects that get their own TOTAL, with their numbers: common = 0, then 1, 2 … in list order. */
export function numberedObjects(site) {
  const single = isSingleBuilding(site);
  const ents = single ? site.ents.filter((e) => e.kind === "bldg") : site.ents;
  let next = 1;
  return ents.map((e) => ({ ...e, number: e.kind === "common" ? 0 : next++ }));
}

export function tabPrefix(site, objectNumber) {
  return isSingleBuilding(site) ? "" : `${objectNumber}-`;
}

export function totalSheetName(site, objectNumber) {
  return isSingleBuilding(site) ? "TOTAL" : `${objectNumber}-TOTAL`;
}

export function isLinked(site, ent) {
  return !!(ent && ent.typ && ent.link && site.ents.some((e) => e.id === ent.typ));
}

export const COMMON_ENTITY = { id: "c0", kind: "common", name: "საერთო — ეზო და ქსელები", tpl: "site" };
export const PARKING_ENTITY = { id: "p1", kind: "shared", name: "მიწისქვეშა პარკინგი", tpl: "parking", serves: [], down: 1 };

/** A new project: one building; common works and parking are ticked on in the plot step. */
export function defaultSite({ plotName = "ნაკვეთი", floorsAbove = 9, floorsBelow = 1 } = {}) {
  return {
    name: plotName,
    area: "",
    naming: { rule: "კორპუსი 1, კორპუსი 2" },
    ents: [{ id: "e1", kind: "bldg", name: "კორპუსი 1", tpl: "building", zones: 1, down: floorsBelow, up: floorsAbove, typ: null, link: false }],
  };
}

// ---------- template → tree ----------

const toNode = (t, includeOptional) => ({
  id: `n_${t.id}`,
  lib: t.id,
  name: t.name_ka,
  en: t.name_en || "",
  cat: t.category || "",
  sys: t.kind === "system",
  on: true,
  hidden: false,
  split: true,
  sheet: t.sheet_code || "",
  loc: t.location_rule || "",
  kids: (t.children || []).filter((k) => includeOptional || k.in_preset !== false).map((k) => toNode(k, includeOptional)),
});

const pickIds = (list, keep) =>
  list
    .filter((t) => keep.includes(t.id))
    .map((t) => (t.children ? { ...t, children: pickIds(t.children, keep) } : t));

/**
 * The starting cost structure of an object from the template (residential_template_R04 shape).
 * kind: "building" | "site" (common object 0) | "parking". A building standing alone takes the whole
 * template; next to others it takes presets.building_with_others (R04: Hard Cost 1–5 and 8; 6, 7, Soft Cost and OPEX go to object 0).
 */
export function presetTree(template, kind, { single = false } = {}) {
  const tree = template?.tree || [];
  let picked;
  if (kind === "parking") picked = template?.parking_tree || [];
  else if (kind === "site") picked = tree.filter((t) => (template.presets?.common_object_0 || []).includes(t.id));
  else if (single) picked = tree;
  else {
    const keep = template?.presets?.building_with_others;
    picked = Array.isArray(keep) ? tree.filter((t) => keep.includes(t.id)) : tree.filter((t) => t.category === "hard");
  }
  return picked.map((t) => toNode(t, false));
}

/** Library rows (template incl. optional systems) for the editor's right-hand pane. */
export function libraryTree(template) {
  return (template?.tree || []).map((t) => toNode(t, true));
}

export function initTrees(site, template) {
  const single = isSingleBuilding(site);
  const trees = {};
  site.ents.forEach((e) => {
    if (isLinked(site, e)) return;
    trees[e.id] = presetTree(template, e.kind === "bldg" ? "building" : e.tpl, { single });
  });
  return trees;
}

// ---------- tree helpers ----------

export function findNode(tree, id, parent = null, depth = 1) {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].id === id) return { list: tree, index: i, node: tree[i], parent, depth };
    const hit = findNode(tree[i].kids, id, tree[i], depth + 1);
    if (hit) return hit;
  }
  return null;
}

export const walkTree = (tree, fn, depth = 1, parent = null) =>
  tree.forEach((n) => {
    fn(n, depth, parent);
    walkTree(n.kids, fn, depth + 1, n);
  });

const clone = (x) => JSON.parse(JSON.stringify(x));

// ---------- numbering ----------

/**
 * Row numbers as in the template (1, 2.2, 4.1.1), per object. Automatic: switched-off rows get no
 * number and the rest close up. Frozen: every row keeps the number stored in `no`.
 */
export function codes(tree, frozen = false) {
  const out = {};
  const walk = (list, depth, prefix, parentOn) => {
    let n = 0;
    list.forEach((x) => {
      const on = parentOn && x.on !== false;
      let no = null;
      if (frozen && x.no != null) no = x.no;
      else if (!frozen && on) no = ++n;
      const code = no == null ? null : depth === 1 ? String(no) : `${prefix}.${no}`;
      out[x.id] = code;
      walk(x.kids, depth + 1, code || prefix, on);
    });
  };
  walk(tree, 1, "", true);
  return out;
}

/** Freezing keeps today's numbers; switched-off rows get the next free ones. Returns { tree, next }. */
export function freeze(tree) {
  const t = clone(tree);
  const next = {};
  const walk = (list, key, parentOn) => {
    let n = 0;
    list.forEach((x) => {
      x.no = parentOn && x.on !== false ? ++n : null;
      x.pno = key;
    });
    list.forEach((x) => {
      if (x.no == null) x.no = ++n;
      walk(x.kids, x.id, parentOn && x.on !== false);
    });
    next[key] = n;
  };
  walk(t, "root", true);
  return { tree: t, next };
}

/**
 * Frozen numbering after an edit (mutates `tree`): a row new to its parent gets the parent's next
 * free number; numbers are never reused. Returns the updated `next` map.
 */
export function renumberFrozen(tree, next) {
  const nx = { ...next };
  const walk = (list, key) => {
    let max = list.reduce((m, x) => (x.pno === key && x.no != null ? Math.max(m, x.no) : m), nx[key] || 0);
    list.forEach((x) => {
      if (x.pno !== key || x.no == null) {
        x.no = ++max;
        x.pno = key;
      }
      walk(x.kids, x.id);
    });
    nx[key] = max;
  };
  walk(tree, "root");
  return nx;
}

// ---------- categories, roles, location ----------

/** Cost category per top-level row; a row without its own takes the one above it. */
export function categories(tree) {
  const out = {};
  let last = "hard";
  tree.forEach((n) => {
    if (n.cat) last = n.cat;
    out[n.id] = last;
  });
  return out;
}

const hasSubDisciplines = (n) => n.kids.some((k) => k.on !== false && !k.sys);

/**
 * Excel role of every row: "sum" (Σ line on the TOTAL), "sheet" (own sheet + TOTAL line),
 * "heading" (heading inside a "one sheet" discipline), "block" (system), or null (off / hidden).
 */
export function excelRoles(tree) {
  const out = {};
  const walk = (list, inSheet, visible) =>
    list.forEach((n) => {
      const shown = visible && n.on !== false && !n.hidden;
      if (!shown) out[n.id] = null;
      else if (n.sys) out[n.id] = "block";
      else if (inSheet) out[n.id] = "heading";
      else if (n.split !== false && hasSubDisciplines(n)) out[n.id] = "sum";
      else out[n.id] = "sheet";
      walk(n.kids, inSheet || out[n.id] === "sheet", shown);
    });
  walk(tree, false, true);
  return out;
}

export function normalizeLocation(rule) {
  if (LOCATION_RULES[rule]) return rule;
  return { room: "ztype", zone: "ztype", fzone: "floor", ftype: "floor", elev: "vol" }[rule] || "none";
}

/** Location rule of a row: its own, else inherited from its parent; top level without one = object. */
export function effectiveLocation(tree, id) {
  const hit = findNode(tree, id);
  if (!hit) return "none";
  if (hit.node.loc) return normalizeLocation(hit.node.loc);
  if (hit.depth === 1) return "none";
  return effectiveLocation(tree, hit.parent.id);
}

// ---------- sheets ----------

/**
 * Workbook sheets of one object: every visible discipline without sub-disciplines (or set to
 * "one sheet") is a sheet. Tab = [object-]code_SHORT; a top-level discipline's code gets ".0".
 * Returns [{ id, tab, code, err, cut }] — err: forbidden chars, too long or duplicate.
 */
export function sheetList(tree, rowCodes, prefix = "") {
  const out = [];
  const walk = (list, depth) =>
    list
      .filter((n) => n.on !== false && !n.hidden && !n.sys)
      .forEach((n) => {
        if (n.split !== false && hasSubDisciplines(n)) {
          walk(n.kids, depth + 1);
          return;
        }
        const code = (rowCodes[n.id] || "") + (depth === 1 ? ".0" : "");
        const short = (n.sheet || "").trim();
        const head = prefix + code + (short ? "_" : " ");
        let tab;
        let err = null;
        let cut = false;
        if (short) {
          tab = head + short;
          if (FORBIDDEN_SHEET_CHARS.test(short)) err = "აკრძალული სიმბოლო — [ ] : * ? / \\";
          else if (tab.length > SHEET_NAME_MAX) err = `სახელი ${tab.length} სიმბოლოა, Excel-ში მაქსიმუმ ${SHEET_NAME_MAX}`;
        } else {
          tab = head + n.name.replace(FORBIDDEN_SHEET_CHARS_G, "");
          if (tab.length > SHEET_NAME_MAX) {
            tab = `${tab.slice(0, SHEET_NAME_MAX - 1).trim()}…`;
            cut = true;
          }
        }
        out.push({ id: n.id, tab, code, err, cut });
      });
  walk(tree, 1);
  const seen = {};
  out.forEach((s) => {
    const key = s.tab.toLowerCase();
    if (seen[key]) {
      s.err = s.err || "ასეთი ფურცელი უკვე არის";
      seen[key].err = seen[key].err || "ასეთი ფურცელი უკვე არის";
    } else seen[key] = s;
  });
  return out;
}

/** Every sheet of the workbook, object by object (TOTAL first). Linked buildings use their source's tree. */
export function workbookSheets(site, trees, { frozen = false } = {}) {
  return numberedObjects(site).map((obj) => {
    const source = isLinked(site, obj) ? obj.typ : obj.id;
    const tree = trees[source] || [];
    const prefix = tabPrefix(site, obj.number);
    return {
      objectId: obj.id,
      number: obj.number,
      name: obj.name,
      total: totalSheetName(site, obj.number),
      sheets: sheetList(tree, codes(tree, frozen), prefix),
    };
  });
}

// ---------- editing rules ----------

const kindOf = (n) => (n.sys ? "sys" : "disc");

/**
 * A discipline holds either sub-disciplines or systems, never both; a system holds nothing;
 * the top level holds disciplines only. `exceptIds`: rows being moved away from `parent`.
 */
export function canHold(parent, kind, exceptIds = []) {
  if (!parent) return kind === "disc";
  if (parent.sys) return false;
  const others = parent.kids.filter((k) => !exceptIds.includes(k.id));
  return !others.length || kindOf(others[0]) === kind;
}

/** Structural problems of a tree: [{ id, error }]. Empty = valid. */
export function validateTree(tree) {
  const errors = [];
  tree.forEach((n) => {
    if (n.sys) errors.push({ id: n.id, error: "ზედა დონეზე მხოლოდ დისციპლინაა" });
  });
  walkTree(tree, (n) => {
    if (!String(n.name || "").trim()) errors.push({ id: n.id, error: "დასახელება ცარიელია" });
    if (n.sys && n.kids.length) errors.push({ id: n.id, error: "სისტემა სხვა სტრიქონს ვერ შეიცავს" });
    if (!n.sys && n.kids.some((k) => k.sys) && n.kids.some((k) => !k.sys))
      errors.push({ id: n.id, error: "დისციპლინაში ან ქვე-დისციპლინებია, ან სისტემები — ორივე ერთად არა" });
  });
  return errors;
}

/**
 * An editor tree back in template format (for saving it as the default template). Optional
 * library rows of the previous template that are not in the tree are kept as optional, so
 * the library does not lose them.
 */
export function nodesToTemplate(tree, previousTemplateTree = []) {
  const previous = {};
  const index = (list) =>
    list.forEach((t) => {
      previous[t.id] = t;
      index(t.children || []);
    });
  index(previousTemplateTree);
  const used = new Set();
  walkTree(tree, (n) => used.add(n.lib || n.id));
  const rowCodes = codes(tree);
  const convert = (n, depth) => {
    const id = n.lib || n.id;
    const kept = (previous[id]?.children || []).filter((c) => c.in_preset === false && !used.has(c.id));
    const children = [...n.kids.filter((k) => k.on !== false).map((k) => convert(k, depth + 1)), ...kept];
    return Object.fromEntries(
      Object.entries({
        id,
        code: rowCodes[n.id] || "",
        kind: n.sys ? "system" : "discipline",
        name_ka: n.name,
        name_en: n.en || undefined,
        sheet_code: n.sheet || undefined,
        category: depth === 1 ? n.cat || undefined : undefined,
        location_rule: n.loc || undefined,
        children: children.length ? children : undefined,
      }).filter(([, v]) => v !== undefined)
    );
  };
  return tree.filter((n) => n.on !== false).map((n) => convert(n, 1));
}
